import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";

const TOTAL_PAGES = 66;
const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const AUTO_PLAY_INTERVAL = 15000; // 15 Sekunden
const MOBILE_BREAKPOINT = 768;

function App() {
  const [currentPage, setCurrentPage] = useState(1); // Single page tracking
  const [scale, setScale] = useState(1);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const autoPlayRef = useRef(null);

  // Check if we should show single page (mobile or cover/last page on desktop)
  const isDesktopSinglePage = !isMobile && (currentPage === 1 || currentPage === TOTAL_PAGES);
  const showSinglePage = isMobile || isDesktopSinglePage;
  
  // For desktop double-page: calculate the spread
  const getSpreadPages = () => {
    if (showSinglePage) return [currentPage];
    // For even pages, show current and next
    // For odd pages (except 1), show previous and current
    if (currentPage % 2 === 0) {
      return [currentPage, Math.min(currentPage + 1, TOTAL_PAGES - 1)];
    } else {
      return [currentPage - 1, currentPage];
    }
  };
  
  const spreadPages = getSpreadPages();

  const getPageUrl = (page) => {
    return `/epub/Lookbook_Bodyfit_Couture-${page}.xhtml`;
  };

  // Check if mobile on resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate scale based on viewport
  useEffect(() => {
    const calculateScale = () => {
      const headerHeight = isMobile ? 55 : 70;
      const footerHeight = isMobile ? 60 : 70;
      const padding = isMobile ? 20 : 30;
      const navWidth = isMobile ? 80 : 160;
      
      const availableHeight = window.innerHeight - headerHeight - footerHeight - padding;
      const availableWidth = window.innerWidth - navWidth;
      
      // Mobile: always single page width, Desktop: depends on spread
      const contentWidth = showSinglePage ? PAGE_WIDTH : (PAGE_WIDTH * 2 + 20);
      
      const scaleByHeight = availableHeight / PAGE_HEIGHT;
      const scaleByWidth = availableWidth / contentWidth;
      
      const newScale = Math.min(scaleByHeight, scaleByWidth, 1.2);
      setScale(Math.max(0.3, newScale));
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [showSinglePage, isMobile]);

  // Auto-play
  useEffect(() => {
    if (isPlaying) {
      autoPlayRef.current = setInterval(() => {
        setCurrentPage((prev) => {
          if (isMobile) {
            // Mobile: advance one page at a time
            return prev >= TOTAL_PAGES ? 1 : prev + 1;
          } else {
            // Desktop: advance by 2 for spreads, 1 for cover/last
            if (prev === 1) return 2; // From cover to first spread
            if (prev >= TOTAL_PAGES - 1) return 1; // Loop back
            return prev + 2; // Advance by spread
          }
        });
      }, AUTO_PLAY_INTERVAL);
    }
    
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isPlaying, isMobile]);

  const handleUserInteraction = () => {
    setIsPlaying(false);
    setTimeout(() => setIsPlaying(true), 30000);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const goNext = useCallback(() => {
    handleUserInteraction();
    setCurrentPage((prev) => {
      if (isMobile) {
        return Math.min(prev + 1, TOTAL_PAGES);
      } else {
        // Desktop: navigate by spread
        if (prev === 1) return 2;
        if (prev >= TOTAL_PAGES - 1) return TOTAL_PAGES;
        return Math.min(prev + 2, TOTAL_PAGES);
      }
    });
  }, [isMobile]);

  const goPrev = useCallback(() => {
    handleUserInteraction();
    setCurrentPage((prev) => {
      if (isMobile) {
        return Math.max(prev - 1, 1);
      } else {
        // Desktop: navigate by spread
        if (prev === TOTAL_PAGES) return TOTAL_PAGES - 1;
        if (prev <= 2) return 1;
        return Math.max(prev - 2, 1);
      }
    });
  }, [isMobile]);

  const goToStart = () => {
    handleUserInteraction();
    setCurrentPage(1);
  };

  const goToEnd = () => {
    handleUserInteraction();
    setCurrentPage(TOTAL_PAGES);
  };

  const isAtStart = currentPage === 1;
  const isAtEnd = isMobile ? currentPage === TOTAL_PAGES : currentPage >= TOTAL_PAGES - 1;

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goNext, goPrev]);

  // Hide badges
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      [class*="emergent"], [id*="emergent"], 
      [class*="Emergent"], [id*="Emergent"] {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const getPageDisplay = () => {
    if (showSinglePage) {
      return `Seite ${currentPage}`;
    }
    const pages = spreadPages;
    return `Seiten ${pages[0]}-${pages[1]}`;
  };
  
  const getProgress = () => {
    return (currentPage / TOTAL_PAGES) * 100;
  };

  return (
    <div className="viewer-app" data-testid="viewer-app">
      {/* Header - Logo Only */}
      <header className="viewer-header minimal" data-testid="viewer-header">
        <img 
          src="/bodyfit-logo-transparent.png" 
          alt="Bodyfit Couture" 
          className="header-logo-center"
        />
      </header>

      {/* Main Book Viewer */}
      <main className="viewer-main">
        <button 
          onClick={goPrev} 
          className="page-nav prev" 
          disabled={isAtStart}
          data-testid="prev-btn"
        >
          <ChevronLeft size={isMobile ? 24 : 36} />
        </button>

        <div 
          className={`book-container ${showSinglePage ? 'single' : 'double'}`}
          style={{ transform: `scale(${scale})` }}
        >
          {showSinglePage ? (
            /* Single Page - Mobile or Cover/Last on Desktop */
            <div className="page-wrapper single-page">
              <iframe
                key={`single-${currentPage}`}
                src={getPageUrl(currentPage)}
                className="page-iframe"
                title={`Seite ${currentPage}`}
                scrolling="no"
              />
              <div className="page-number center">{currentPage}</div>
            </div>
          ) : (
            /* Double Page - Desktop only */
            <>
              <div className="book-spine"></div>
              <div className="page-wrapper left-page">
                <iframe
                  key={`left-${spreadPages[0]}`}
                  src={getPageUrl(spreadPages[0])}
                  className="page-iframe"
                  title={`Seite ${spreadPages[0]}`}
                  scrolling="no"
                />
                <div className="page-number left">{spreadPages[0]}</div>
              </div>
              <div className="page-wrapper right-page">
                <iframe
                  key={`right-${spreadPages[1]}`}
                  src={getPageUrl(spreadPages[1])}
                  className="page-iframe"
                  title={`Seite ${spreadPages[1]}`}
                  scrolling="no"
                />
                <div className="page-number right">{spreadPages[1]}</div>
              </div>
            </>
          )}
        </div>

        <button 
          onClick={goNext} 
          className="page-nav next" 
          disabled={isAtEnd}
          data-testid="next-btn"
        >
          <ChevronRight size={isMobile ? 24 : 36} />
        </button>
      </main>

      {/* Footer */}
      <footer className="viewer-footer" data-testid="viewer-footer">
        <div className="footer-controls">
          <button onClick={goToStart} className="jump-btn" disabled={isAtStart}>
            ‹‹
          </button>
          <button onClick={goPrev} className="jump-btn" disabled={isAtStart}>
            ‹
          </button>
          
          <button onClick={togglePlayPause} className="play-btn" data-testid="play-pause-btn">
            {isPlaying ? <Pause size={16} /> : <Play size={16} />}
          </button>
          
          <div className="page-indicator">
            <span className="page-info">{getPageDisplay()}</span>
            <span className="page-divider">|</span>
            <span className="page-total">{TOTAL_PAGES}</span>
          </div>

          <button onClick={goNext} className="jump-btn" disabled={isAtEnd}>
            ›
          </button>
          <button onClick={goToEnd} className="jump-btn" disabled={isAtEnd}>
            ››
          </button>
        </div>
        
        {/* Progress bar */}
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${getProgress()}%` }}
          />
        </div>
      </footer>
    </div>
  );
}

export default App;
